import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Input,
  Table,
  Button,
  Chip,
  Sheet,
  Select,
  Option,
} from '@mui/joy';
import {
  ChevronLeft,
  ChevronRight,
  ArrowUp,
  ArrowDown,
  Download,
  FileSpreadsheet,
  RefreshCcw,
  Activity,
  Globe,
  Chrome,
  Monitor,
} from 'lucide-react';
import { authFetch } from '../../utils/authFetch';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';

export default function LoginLogs() {
  const selectedOrg = localStorage.getItem('selectedOrg');

  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);

  const perPage = 100;

  const [sortConfig, setSortConfig] = useState({
    key: 'timestamp',
    direction: 'desc',
  });

  const parseBody = useCallback((body) => {
    try {
      if (!body) return {};
      if (typeof body === 'object') return body;
      return JSON.parse(body);
    } catch {
      return {};
    }
  }, []);

  const getUsername = useCallback((log) => {
    const parsed = parseBody(log.body);
    return parsed?.body?.username || '—';
  }, [parseBody]);

  const getUserAgent = useCallback((log) => {
    const parsed = parseBody(log.body);
    return parsed?.headers?.['user-agent'] || '';
  }, [parseBody]);

  const getBrowser = useCallback((userAgent = '') => {
    const ua = userAgent.toLowerCase();

    if (ua.includes('dart/')) return 'App mobile';
    if (ua.includes('edg/')) return 'Edge';
    if (ua.includes('firefox/')) return 'Firefox';
    if (ua.includes('opr/') || ua.includes('opera/')) return 'Opera';
    if (ua.includes('chrome/') && !ua.includes('edg/')) return 'Chrome';
    if (ua.includes('safari/') && !ua.includes('chrome/')) return 'Safari';

    return 'Altro';
  }, []);

  const BrowserIcon = ({ browser }) => {
    switch (browser) {
      case 'Chrome':
        return <Chrome size={14} />;
      case 'Firefox':
      case 'Edge':
      case 'Safari':
      case 'Opera':
        return <Globe size={14} />;
      default:
        return <Monitor size={14} />;
    }
  };

  const normalize = (v) => (v ?? '').toString().toLowerCase();

  const fetchLogs = useCallback(async () => {
    if (!selectedOrg) return;

    setLoading(true);

    try {
      const offset = (page - 1) * perPage;

      const res = await authFetch(
        `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/log/accessi?limit=${perPage}&offset=${offset}`,
        { credentials: 'include' }
      );

      if (!res.ok) throw new Error('Errore fetch accessi logs');

      const result = await res.json();

      const rows = Array.isArray(result)
        ? result
        : Array.isArray(result.data)
        ? result.data
        : [];

      setLogs(rows);
      setTotal(Number(result.total) || rows.length);
    } catch (err) {
      console.error('Errore nel fetch accessi logs:', err);
      setLogs([]);
      setTotal(0);

      toast.error('Errore nel caricamento dei log accessi', {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, page]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    if (isNaN(d)) return '';

    return `${d.toLocaleDateString('it-IT')} ${d.toLocaleTimeString('it-IT', {
      hour: '2-digit',
      minute: '2-digit',
    })}`;
  };

  const getStatusColor = (status) => {
    const code = Number(status);

    if (code >= 500) return 'danger';
    if (code >= 400) return 'warning';
    if (code >= 200 && code < 300) return 'success';

    return 'neutral';
  };

  const getLoginResult = (status) => {
    const code = Number(status);

    if (code >= 200 && code < 300) return 'Riuscito';
    if (code === 401) return 'Credenziali errate';
    if (code >= 400) return 'Fallito';

    return '—';
  };

  const filteredLogs = useMemo(() => {
    const term = normalize(searchTerm);

    return logs.filter((log) => {
      if (!term) return true;

      const username = getUsername(log);
      const browser = getBrowser(getUserAgent(log));
      const result = getLoginResult(log.response_status);

      return (
        normalize(log.id).includes(term) ||
        normalize(username).includes(term) ||
        normalize(log.url).includes(term) ||
        normalize(log.ip).includes(term) ||
        normalize(browser).includes(term) ||
        normalize(result).includes(term) ||
        normalize(log.response_status).includes(term) ||
        normalize(log.timestamp).includes(term)
      );
    });
  }, [logs, searchTerm, getUsername, getBrowser, getUserAgent]);

  const sortedLogs = useMemo(() => {
    const { key, direction } = sortConfig;
    const dir = direction === 'asc' ? 1 : -1;

    const getComparable = (item) => {
      if (key === 'username') return normalize(getUsername(item));
      if (key === 'browser') return normalize(getBrowser(getUserAgent(item)));
      if (key === 'result') return normalize(getLoginResult(item.response_status));

      const val = item?.[key];

      if (key === 'id' || key === 'response_status' || key === 'response_time_ms') {
        return Number(val) || 0;
      }

      if (key === 'timestamp') {
        const t = val ? new Date(val).getTime() : 0;
        return Number.isFinite(t) ? t : 0;
      }

      return normalize(val);
    };

    return [...filteredLogs].sort((a, b) => {
      const A = getComparable(a);
      const B = getComparable(b);

      if (A < B) return -1 * dir;
      if (A > B) return 1 * dir;
      return 0;
    });
  }, [filteredLogs, sortConfig, getUsername, getBrowser, getUserAgent]);

  const handleSort = (key) => {
    setSortConfig((cur) => {
      if (cur.key === key) {
        return {
          key,
          direction: cur.direction === 'asc' ? 'desc' : 'asc',
        };
      }

      return { key, direction: 'asc' };
    });
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowDown size={14} color="#bbb" />;
    }

    return sortConfig.direction === 'asc' ? (
      <ArrowUp size={14} color="black" />
    ) : (
      <ArrowDown size={14} color="black" />
    );
  };

  const getTimestampForFile = () => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');

    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
      now.getDate()
    )}_${pad(now.getHours())}-${pad(now.getMinutes())}`;
  };

  const getExportRows = () => {
    return sortedLogs.map((log) => ({
      ID: log.id ?? '',
      Username: getUsername(log),
      URL: log.url ?? '',
      Browser: getBrowser(getUserAgent(log)),
      Esito: getLoginResult(log.response_status),
      Stato: log.response_status ?? '',
      IP: log.ip ?? '',
      'Tempo risposta ms': log.response_time_ms ?? '',
      Data: formatDate(log.timestamp),
    }));
  };

  const exportCSV = () => {
    try {
      const rows = getExportRows();

      if (!rows.length) {
        toast.info('Nessun dato da esportare', {
          position: 'top-right',
          autoClose: 2500,
        });
        return;
      }

      const headers = Object.keys(rows[0]);

      const escapeCsvValue = (value) => {
        const stringValue = value == null ? '' : String(value);
        const escaped = stringValue.replace(/"/g, '""');
        return `"${escaped}"`;
      };

      const csvContent = [
        headers.join(';'),
        ...rows.map((row) =>
          headers.map((header) => escapeCsvValue(row[header])).join(';')
        ),
      ].join('\n');

      const blob = new Blob([`\uFEFF${csvContent}`], {
        type: 'text/csv;charset=utf-8;',
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.setAttribute('download', `login_logs_${getTimestampForFile()}.csv`);

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);

      toast.success('CSV esportato con successo', {
        position: 'top-right',
        autoClose: 2500,
      });
    } catch (err) {
      console.error('Errore export CSV:', err);

      toast.error('Errore durante l’esportazione CSV', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  const exportExcel = () => {
    try {
      const rows = getExportRows();

      if (!rows.length) {
        toast.info('Nessun dato da esportare', {
          position: 'top-right',
          autoClose: 2500,
        });
        return;
      }

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(workbook, worksheet, 'Login logs');
      XLSX.writeFile(workbook, `login_logs_${getTimestampForFile()}.xlsx`);

      toast.success('Excel esportato con successo', {
        position: 'top-right',
        autoClose: 2500,
      });
    } catch (err) {
      console.error('Errore export Excel:', err);

      toast.error('Errore durante l’esportazione Excel', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const hasNextPage = page < totalPages;

  return (
    <Box>
      <Typography level="h3" mb={2}>
        Log accessi
      </Typography>

      <Sheet
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 'lg',
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Input
            placeholder="Cerca per username, IP, browser, esito..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ width: { xs: '100%', sm: 420 } }}
          />

          <Typography level="body-sm" color="neutral">
            {sortedLogs.length} record mostrati su {total}
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Button
            variant="outlined"
            color="neutral"
            startDecorator={<RefreshCcw size={16} />}
            onClick={fetchLogs}
            disabled={loading}
          >
            Aggiorna
          </Button>

          <Button
            variant="outlined"
            color="neutral"
            startDecorator={<Download size={16} />}
            onClick={exportCSV}
            disabled={loading || sortedLogs.length === 0}
          >
            Esporta CSV
          </Button>

          <Button
            variant="outlined"
            color="success"
            startDecorator={<FileSpreadsheet size={16} />}
            onClick={exportExcel}
            disabled={loading || sortedLogs.length === 0}
          >
            Esporta Excel
          </Button>
        </Box>
      </Sheet>

      {loading ? (
        <Typography>Caricamento...</Typography>
      ) : sortedLogs.length === 0 ? (
        <Typography>Nessun log di accesso trovato.</Typography>
      ) : (
        <Box
          sx={{
            borderRadius: 'lg',
            overflow: 'auto',
            border: '1px solid',
            borderColor: 'neutral.outlinedBorder',
          }}
        >
          <Table
            size="sm"
            sx={{
              minWidth: { xs: 1100, sm: 1100 },
              tableLayout: 'auto',
              '& thead th': {
                bgcolor: 'neutral.softBg',
                textAlign: 'left',
                px: { xs: 1, sm: 2 },
                py: { xs: 0.75, sm: 1 },
                fontSize: { xs: 'xs', sm: 'sm' },
                whiteSpace: 'nowrap',
                cursor: 'pointer',
              },
              '& tbody td': {
                px: { xs: 1, sm: 2 },
                py: { xs: 0.75, sm: 1 },
                fontSize: { xs: 'sm', sm: 'md' },
                lineHeight: 1.2,
                verticalAlign: 'middle',
              },
              '& tbody tr:hover': {
                bgcolor: 'neutral.plainHoverBg',
              },
            }}
          >
            <thead>
              <tr>
                <th onClick={() => handleSort('id')}>
                  <Box display="flex" alignItems="center" gap={1}>
                    ID <SortIcon columnKey="id" />
                  </Box>
                </th>

                <th onClick={() => handleSort('username')}>
                  <Box display="flex" alignItems="center" gap={1}>
                    Username <SortIcon columnKey="username" />
                  </Box>
                </th>

                <th onClick={() => handleSort('url')}>
                  <Box display="flex" alignItems="center" gap={1}>
                    URL <SortIcon columnKey="url" />
                  </Box>
                </th>

                <th onClick={() => handleSort('browser')}>
                  <Box display="flex" alignItems="center" gap={1}>
                    Browser <SortIcon columnKey="browser" />
                  </Box>
                </th>

                <th onClick={() => handleSort('ip')}>
                  <Box display="flex" alignItems="center" gap={1}>
                    IP <SortIcon columnKey="ip" />
                  </Box>
                </th>

                <th onClick={() => handleSort('result')}>
                  <Box display="flex" alignItems="center" gap={1}>
                    Esito <SortIcon columnKey="result" />
                  </Box>
                </th>

                <th onClick={() => handleSort('response_time_ms')}>
                  <Box display="flex" alignItems="center" gap={1}>
                    Tempo <SortIcon columnKey="response_time_ms" />
                  </Box>
                </th>

                <th onClick={() => handleSort('timestamp')}>
                  <Box display="flex" alignItems="center" gap={1}>
                    Data <SortIcon columnKey="timestamp" />
                  </Box>
                </th>
              </tr>
            </thead>

            <tbody>
              {sortedLogs.map((log) => {
                const username = getUsername(log);
                const userAgent = getUserAgent(log);
                const browser = getBrowser(userAgent);
                const result = getLoginResult(log.response_status);

                return (
                  <tr key={log.id}>
                    <td>{log.id}</td>

                    <td>{username}</td>

                    <td>
                      <Chip
                        size="sm"
                        variant="soft"
                        color="primary"
                        startDecorator={<Activity size={14} />}
                      >
                        {log.url || '—'}
                      </Chip>
                    </td>

                    <td>
                      <Chip
                        size="sm"
                        variant="soft"
                        color="neutral"
                        startDecorator={<BrowserIcon browser={browser} />}
                        title={userAgent}
                      >
                        {browser}
                      </Chip>
                    </td>

                    <td>{log.ip || '—'}</td>

                    <td>
                      <Chip
                        size="sm"
                        variant="soft"
                        color={getStatusColor(log.response_status)}
                      >
                        {result} ({log.response_status || '—'})
                      </Chip>
                    </td>

                    <td>{log.response_time_ms ?? '—'} ms</td>

                    <td>{formatDate(log.timestamp) || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Box>
      )}

      <Box
        mt={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        flexWrap="wrap"
      >
        <Button
          size="sm"
          variant="outlined"
          disabled={page === 1 || loading}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          startDecorator={<ChevronLeft size={14} />}
        >
          Precedente
        </Button>

        <Box display="flex" alignItems="center" gap={1}>
          <Typography level="body-sm">Pagina</Typography>

          <Select
            size="sm"
            value={page}
            onChange={(_, value) => {
              if (value) setPage(value);
            }}
            disabled={loading}
            sx={{ minWidth: 90 }}
          >
            {Array.from({ length: totalPages }, (_, index) => index + 1).map(
              (pageNumber) => (
                <Option key={pageNumber} value={pageNumber}>
                  {pageNumber}
                </Option>
              )
            )}
          </Select>

          <Typography level="body-sm">di {totalPages}</Typography>
        </Box>

        <Button
          size="sm"
          variant="outlined"
          disabled={!hasNextPage || loading}
          onClick={() => setPage((p) => p + 1)}
          endDecorator={<ChevronRight size={14} />}
        >
          Successiva
        </Button>
      </Box>
    </Box>
  );
}